/* ============================================================================
   RYVT runtime — spec §3.4. Exposes window.RYVT:
     on / off / emit                      private UI pub/sub
     track                                allowlisted, destination-neutral DOM event
     cart(action, payload)                the ONLY cart I/O path in the theme
     renderSections(sectionsMap)          C8-scoped morphs
     trapFocus(container) -> release()
     RyvtDrawer                           base class for every drawer/sheet
   Also owns: <ryvt-nav-drawer>, <ryvt-search-sheet>, <ryvt-header> module,
   quick-add delegation, wishlist seam (key 'ryvt:wishlist', ported from
   theme/assets/ryvt-wishlist.js), reveal IO, announcement rotation.
   Vanilla only. No other event names may be minted.
   ========================================================================== */
(() => {
  'use strict';

  if (window.RYVT) return;

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* -- pub/sub (~300B) ----------------------------------------------------- */
  const bus = {};
  function on(event, fn) {
    (bus[event] = bus[event] || []).push(fn);
  }
  function off(event, fn) {
    if (!bus[event]) return;
    bus[event] = bus[event].filter((f) => f !== fn);
  }
  function emit(event, detail) {
    (bus[event] || []).slice().forEach((fn) => {
      try {
        fn(detail);
      } catch (e) {
        /* one bad subscriber never breaks the chain */
      }
    });
  }

  /* -- destination-neutral instrumentation ---------------------------------
     A single public DOM seam keeps the theme independent from pixels, consent
     managers and analytics vendors. The allowlist is also the privacy boundary:
     current callers provide only controlled identifiers, enumerated context,
     counts, cents and booleans — never raw search terms, email addresses, cart
     tokens, line properties or customer data.
     Consumers listen on document for `ryvt:analytics` and route only after their
     own consent rules have run. */
  const TRACK_FIELDS = Object.freeze({
    discovery_select: ['source', 'destination', 'product_handle', 'position'],
    search_submit: ['source', 'query_length'],
    search_results: ['source', 'query_length', 'result_count'],
    search_select: ['source', 'result_type', 'position', 'query_length'],
    product_view: ['product_id', 'product_handle', 'variant_id', 'available'],
    variant_select: ['product_id', 'product_handle', 'variant_id', 'available'],
    size_guide_view: ['product_id', 'product_handle', 'tab'],
    size_recommender_complete: ['product_id', 'product_handle', 'recommended_size'],
    size_recommender_apply: ['product_id', 'product_handle', 'recommended_size'],
    wishlist_toggle: ['product_handle', 'saved', 'item_count', 'source'],
    cart_add: ['variant_id', 'quantity', 'item_count', 'source'],
    cart_mutation: ['action', 'item_count', 'source'],
    offer_allocation: ['allocation_count', 'total_discount', 'item_count'],
    checkout_start: ['source'],
    newsletter_submit: ['source'],
  });

  function track(name, fields) {
    const allowed = TRACK_FIELDS[name];
    if (!allowed || typeof CustomEvent !== 'function') return;
    const detail = { event: name, version: 1 };
    const input = fields && typeof fields === 'object' ? fields : {};
    allowed.forEach((key) => {
      const value = input[key];
      if (typeof value === 'string' && value !== '') detail[key] = value.slice(0, 120);
      else if (typeof value === 'boolean') detail[key] = value;
      else if (typeof value === 'number' && Number.isFinite(value)) detail[key] = value;
    });
    document.dispatchEvent(new CustomEvent('ryvt:analytics', { detail }));
  }

  function trackCartResult(action, payload, data, source) {
    if (!data || typeof data !== 'object') return;
    const cartData = document.querySelector('[data-ryvt-cart-data]');
    const responseItemCount = Number(data.item_count);
    const renderedItemCount = cartData
      ? Number(cartData.getAttribute('data-count'))
      : Number.NaN;
    const itemCount = Number.isFinite(responseItemCount)
      ? responseItemCount
      : Number.isFinite(renderedItemCount)
        ? renderedItemCount
        : undefined;
    if (action === 'add') {
      const items = payload && Array.isArray(payload.items) ? payload.items : [];
      const quantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      track('cart_add', {
        variant_id: items.length === 1 ? Number(items[0].id) : undefined,
        quantity,
        item_count: itemCount,
        source: source || 'unknown',
      });
    } else if (action !== 'sync') {
      track('cart_mutation', {
        action,
        item_count: itemCount,
        source: source || 'unknown',
      });
    }

    const cartDiscounts = Array.isArray(data.cart_level_discount_applications)
      ? data.cart_level_discount_applications
      : [];
    const lineDiscounts = Array.isArray(data.items)
      ? data.items.flatMap((item) =>
          Array.isArray(item.line_level_discount_allocations)
            ? item.line_level_discount_allocations
            : []
        )
      : [];
    const cartAllocations = cartDiscounts.length;
    const lineAllocations = lineDiscounts.length;
    const allocationCount = cartAllocations + lineAllocations;
    const responseTotalDiscount = Number(data.total_discount);
    const allocationTotal = cartDiscounts
      .concat(lineDiscounts)
      .reduce(
        (sum, allocation) =>
          sum + Number(allocation.total_allocated_amount || allocation.amount || 0),
        0
      );
    const totalDiscount = Number.isFinite(responseTotalDiscount)
      ? responseTotalDiscount
      : allocationTotal;
    if (allocationCount > 0 || totalDiscount > 0) {
      track('offer_allocation', {
        allocation_count: allocationCount,
        total_discount: Number.isFinite(totalDiscount) ? totalDiscount : 0,
        item_count: itemCount,
      });
    }
  }

  /* -- helpers -------------------------------------------------------------- */
  function crossfadeText(el, text) {
    if (!el) return;
    const next = text == null ? '' : String(text);
    if (el.textContent === next) return;
    if (reducedMotionQuery.matches) {
      el.textContent = next;
      return;
    }
    el.classList.add('is-fading');
    window.setTimeout(() => {
      el.textContent = next;
      el.classList.remove('is-fading');
    }, 120);
  }

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container) {
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      const nodes = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKeydown);
    return () => container.removeEventListener('keydown', onKeydown);
  }

  /* -- cart helper (Section Rendering, one round trip) ---------------------- */
  const CART_SECTIONS = 'ryvt-cart-drawer,ryvt-header';

  function cart(action, payload, source) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const body = Object.assign({}, payload, {
      sections: CART_SECTIONS,
      sections_url: window.location.pathname,
    });
    return fetch(`${root}cart/${action}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          return Promise.reject({
            message: (data && (data.description || data.message)) || 'Something went wrong.',
          });
        }
        renderSections(data.sections || {});
        if (!data.sections || !data.sections['ryvt-header']) {
          const count = typeof data.item_count === 'number' ? data.item_count : null;
          if (count !== null) {
            document.querySelectorAll('[data-ryvt-cart-count]').forEach((el) => {
              crossfadeText(el, count > 0 ? String(count) : '');
            });
          }
        }
        emit('ryvt:cart:change', { cart: data, action });
        trackCartResult(action, payload, data, source);
        return data;
      });
  }

  function renderSections(sections) {
    if (!sections) return;
    const parser = new DOMParser();

    if (sections['ryvt-cart-drawer']) {
      const doc = parser.parseFromString(sections['ryvt-cart-drawer'], 'text/html');
      const scope =
        document.querySelector('[data-ryvt-section="ryvt-cart-drawer"]') || document;
      const activeId = document.activeElement && document.activeElement.id;
      ['[data-ryvt-cart-lines]', '[data-ryvt-cart-footer]'].forEach((selector) => {
        const src = doc.querySelector(selector);
        const dst = scope.querySelector(selector);
        if (src && dst) dst.replaceChildren(...Array.from(src.childNodes));
      });
      if (activeId) {
        const restore = document.getElementById(activeId);
        if (restore && document.activeElement !== restore) restore.focus({ preventScroll: true });
      }
    }

    if (sections['ryvt-header']) {
      const doc = parser.parseFromString(sections['ryvt-header'], 'text/html');
      const src = doc.querySelector('[data-ryvt-cart-count]');
      if (src) {
        document.querySelectorAll('[data-ryvt-cart-count]').forEach((el) => {
          crossfadeText(el, src.textContent.trim());
        });
      }
    }
  }

  /* -- scrim ---------------------------------------------------------------- */
  function getScrim() {
    return document.querySelector('[data-ryvt-scrim]');
  }

  function anyDrawerOpen() {
    return !!document.querySelector('.ryvt-drawer.is-open');
  }

  /* -- RyvtDrawer base class (lifecycle pattern mandatory for all elements) -- */
  class RyvtDrawer extends HTMLElement {
    connectedCallback() {
      this.reducedMotion = reducedMotionQuery.matches;
      this._onKeydown = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      this._onScrimClick = () => {
        if (this.classList.contains('is-open')) this.close();
      };
      this._onCloseClick = (e) => {
        if (e.target.closest('[data-ryvt-drawer-close]')) this.close();
      };
      document.addEventListener('keydown', this._onKeydown);
      this.addEventListener('click', this._onCloseClick);
      this._scrim = getScrim();
      if (this._scrim) this._scrim.addEventListener('click', this._onScrimClick);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('click', this._onCloseClick);
      if (this._scrim) this._scrim.removeEventListener('click', this._onScrimClick);
      if (this._releaseTrap) this._releaseTrap();
      const wasOpen = this.classList.contains('is-open');
      this.classList.remove('is-open');
      if (this._invoker && this._invoker.setAttribute) this._invoker.setAttribute('aria-expanded', 'false');
      if (wasOpen && !anyDrawerOpen()) {
        document.documentElement.classList.remove('ryvt-scroll-locked');
        document.querySelectorAll('[data-ryvt-inert]').forEach((node) => node.removeAttribute('inert'));
        if (this._scrim) {
          this._scrim.classList.remove('is-open');
          this._scrim.hidden = true;
        }
      }
    }

    get drawerId() {
      return this.id || this.getAttribute('data-ryvt-section') || this.tagName.toLowerCase();
    }

    open(invoker) {
      if (this.classList.contains('is-open')) return;
      this._invoker = invoker || document.activeElement;
      const scrim = this.hasAttribute('data-no-scrim') ? null : this._scrim || getScrim();
      if (scrim) {
        scrim.hidden = false;
        scrim.offsetHeight; /* reflow so the opacity transition runs */
        scrim.classList.add('is-open');
      }
      this.classList.add('is-open');
      document.documentElement.classList.add('ryvt-scroll-locked');
      document.querySelectorAll('[data-ryvt-inert]').forEach((node) => {
        if (!node.contains(this)) node.setAttribute('inert', '');
      });
      if (this._invoker && this._invoker.setAttribute) {
        this._invoker.setAttribute('aria-expanded', 'true');
      }
      const target =
        this.querySelector('[data-drawer-focus]') ||
        this.querySelector('h1, h2, h3, [tabindex="-1"]');
      if (target) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus();
      }
      this._releaseTrap = trapFocus(this);
      emit('ryvt:drawer:open', { id: this.drawerId });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      const scrim = this.hasAttribute('data-no-scrim') ? null : this._scrim || getScrim();
      document.documentElement.classList.remove('ryvt-scroll-locked');
      document.querySelectorAll('[data-ryvt-inert]').forEach((node) => node.removeAttribute('inert'));
      if (this._releaseTrap) {
        this._releaseTrap();
        this._releaseTrap = null;
      }
      if (scrim && !anyDrawerOpen()) {
        scrim.classList.remove('is-open');
        const hideScrim = () => {
          if (!anyDrawerOpen() && !scrim.classList.contains('is-open')) scrim.hidden = true;
        };
        const animations = typeof scrim.getAnimations === 'function' ? scrim.getAnimations() : [];
        if (animations.length) {
          Promise.allSettled(animations.map((animation) => animation.finished)).then(hideScrim);
        } else {
          window.requestAnimationFrame(hideScrim);
        }
      }
      if (this._invoker) {
        if (this._invoker.setAttribute) this._invoker.setAttribute('aria-expanded', 'false');
        if (this._invoker.focus) this._invoker.focus();
        this._invoker = null;
      }
      emit('ryvt:drawer:close', { id: this.drawerId });
    }
  }

  if (!customElements.get('ryvt-drawer')) {
    customElements.define('ryvt-drawer', class extends RyvtDrawer {});
  }

  /* -- nav drawer ----------------------------------------------------------- */
  if (!customElements.get('ryvt-nav-drawer')) {
    customElements.define('ryvt-nav-drawer', class extends RyvtDrawer {});
  }

  /* -- predictive search sheet (lazy-imports the combobox module) ------------ */
  if (!customElements.get('ryvt-search-sheet')) {
    customElements.define(
      'ryvt-search-sheet',
      class extends RyvtDrawer {
        connectedCallback() {
          super.connectedCallback();
          this._onOutsideSearch = (event) => {
            if (!this.classList.contains('is-open') || this.contains(event.target) || this._invoker?.contains(event.target)) return;
            this.close();
          };
          document.addEventListener('pointerdown', this._onOutsideSearch);
        }

        disconnectedCallback() {
          document.removeEventListener('pointerdown', this._onOutsideSearch);
          this.querySelector('ryvt-predictive-search')?.pause?.();
          super.disconnectedCallback();
        }

        open(invoker) {
          if (this.classList.contains('is-open')) return;
          this.removeAttribute('inert');
          this.removeAttribute('aria-hidden');
          super.open(invoker);
          this.querySelector('ryvt-predictive-search')?.resume?.();
          if (!this._moduleLoaded && this.dataset.module) {
            this._moduleLoaded = true;
            import(this.dataset.module).catch(() => {
              this._moduleLoaded = false;
            });
          }
        }

        close() {
          if (!this.classList.contains('is-open')) return;
          this.querySelector('ryvt-predictive-search')?.pause?.();
          super.close();
          // CSS keeps the exit visible; inert removes its controls immediately.
          this.setAttribute('inert', '');
          this.setAttribute('aria-hidden', 'true');
        }
      }
    );
  }

  /* -- wishlist seam (ported from theme/assets/ryvt-wishlist.js) ------------- */
  const WISHLIST_KEY = 'ryvt:wishlist';
  let wishlistMemory = [];
  let wishlistStorageBroken = false;

  function wishlistRead() {
    if (wishlistStorageBroken) return wishlistMemory.slice();
    let raw;
    try {
      raw = window.localStorage.getItem(WISHLIST_KEY);
    } catch (error) {
      wishlistStorageBroken = true;
      return wishlistMemory.slice();
    }
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((handle) => typeof handle === 'string' && handle !== '')
        : [];
    } catch (error) {
      return [];
    }
  }

  function wishlistWrite(list) {
    if (wishlistStorageBroken) {
      wishlistMemory = list.slice();
      return;
    }
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (error) {
      wishlistStorageBroken = true;
      wishlistMemory = list.slice();
    }
  }

  function wishlistEmit() {
    emit('ryvt:wishlist:change', { handles: wishlistRead() });
  }

  /* Account sync (spec: docs/flow/specs/2026-08-09-account-synced-wishlist.md).
     localStorage stays the synchronous read model; a logged-in session mirrors it
     to /apps/wishlist (app proxy → worker → customer metafield). Every failure is
     silent — no proxy, no worker, no network = today's per-device wishlist. The
     synced flag makes the FIRST pull after login a union (nothing saved is lost)
     and every later pull server-wins, so a removal made on another device
     propagates instead of being resurrected by this device's stale copy. */
  const WISHLIST_PROXY = '/apps/wishlist';
  const WISHLIST_SYNCED_KEY = 'ryvt:wishlist:synced';
  const wishlistCustomerSession = document.body.hasAttribute('data-ryvt-customer');
  let wishlistDirty = false;
  let wishlistPushTimer = null;

  function wishlistPush() {
    fetch(WISHLIST_PROXY, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handles: wishlistRead() }),
    }).catch(() => {});
  }

  function wishlistSchedulePush() {
    if (!wishlistCustomerSession) return;
    wishlistDirty = true;
    clearTimeout(wishlistPushTimer);
    wishlistPushTimer = setTimeout(wishlistPush, 800);
  }

  function wishlistPullAndReconcile() {
    if (!wishlistCustomerSession) {
      /* Logged out: clear the flag so the next login unions guest adds back in. */
      try {
        window.localStorage.removeItem(WISHLIST_SYNCED_KEY);
      } catch (error) {}
      return;
    }
    fetch(WISHLIST_PROXY, { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data || !Array.isArray(data.handles)) return;
        /* A mutation happened while the pull was in flight — its pending push
           carries newer state than the server copy; applying the pull would
           silently undo what the customer just clicked. */
        if (wishlistDirty) return;
        const server = data.handles.filter((handle) => typeof handle === 'string' && handle !== '');
        let synced = false;
        try {
          synced = window.localStorage.getItem(WISHLIST_SYNCED_KEY) === '1';
        } catch (error) {}
        const local = wishlistRead();
        const list = synced
          ? server
          : server.concat(local.filter((handle) => server.indexOf(handle) === -1));
        wishlistWrite(list);
        try {
          window.localStorage.setItem(WISHLIST_SYNCED_KEY, '1');
        } catch (error) {}
        wishlistEmit();
        if (!synced && list.length !== server.length) wishlistPush();
      })
      .catch(() => {});
  }

  const wishlist = {
    list() {
      return wishlistRead();
    },
    has(handle) {
      return !!handle && wishlistRead().indexOf(handle) !== -1;
    },
    add(handle) {
      if (!handle) return wishlistRead();
      const list = wishlistRead();
      if (list.indexOf(handle) === -1) {
        list.push(handle);
        wishlistWrite(list);
        wishlistEmit();
        wishlistSchedulePush();
      }
      return list;
    },
    remove(handle) {
      if (!handle) return wishlistRead();
      const next = wishlistRead().filter((existing) => existing !== handle);
      wishlistWrite(next);
      wishlistEmit();
      wishlistSchedulePush();
      return next;
    },
    toggle(handle) {
      return wishlist.has(handle) ? wishlist.remove(handle) : wishlist.add(handle);
    },
  };

  function syncWishlistToggles(handles) {
    document.querySelectorAll('[data-ryvt-wishlist-toggle]').forEach((button) => {
      const saved = handles.indexOf(button.getAttribute('data-ryvt-wishlist-toggle')) !== -1;
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.classList.toggle('is-active', saved);
    });
  }

  on('ryvt:wishlist:change', (detail) => syncWishlistToggles(detail.handles || []));

  /* -- header module --------------------------------------------------------- */
  if (!customElements.get('ryvt-header')) {
    customElements.define(
      'ryvt-header',
      class extends HTMLElement {
        connectedCallback() {
          this._desktopNav = window.matchMedia('(min-width: 990px)');
          this._syncPanelTop = () => {
            const bar = this.querySelector('.ryvt-header__bar');
            if (bar) this.style.setProperty('--ryvt-header-panel-top', `${Math.round(bar.getBoundingClientRect().bottom)}px`);
          };
          this._flyoutItems = () => Array.from(this.querySelectorAll('.ryvt-header__nav-item--has-flyout'));
          this._setFlyout = (item, open) => {
            if (!item) return;
            if (open) {
              this._syncPanelTop();
              const searchSheet = this.querySelector('ryvt-search-sheet');
              if (searchSheet && searchSheet.classList.contains('is-open') && searchSheet.close) searchSheet.close();
              this._flyoutItems().forEach((other) => {
                if (other !== item) this._setFlyout(other, false);
              });
            }
            item.classList.toggle('is-flyout-open', open);
            const trigger = item.querySelector('[data-ryvt-flyout-trigger]');
            if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          };
          this._closeFlyouts = () => this._flyoutItems().forEach((item) => this._setFlyout(item, false));

          this._onClick = (e) => {
            if (e.target.closest('[data-ryvt-flyout-dismiss]')) {
              this._closeFlyouts();
              return;
            }
            const flyoutTrigger = e.target.closest('[data-ryvt-flyout-trigger]');
            if (flyoutTrigger) {
              const item = flyoutTrigger.closest('.ryvt-header__nav-item--has-flyout');
              this._setFlyout(item, !item.classList.contains('is-flyout-open'));
              return;
            }
            const navBtn = e.target.closest('[data-ryvt-nav-open]');
            if (navBtn) {
              const drawer = this.querySelector('ryvt-nav-drawer');
              if (drawer && drawer.open) drawer.open(navBtn);
              return;
            }
            const searchBtn = e.target.closest('[data-ryvt-search-open]');
            if (searchBtn) {
              this._syncPanelTop();
              this._closeFlyouts();
              const sheet = this.querySelector('ryvt-search-sheet');
              if (sheet && sheet.open) sheet.open(searchBtn);
            }
          };
          this.addEventListener('click', this._onClick);

          this._onPointerOver = (e) => {
            if (!this._desktopNav.matches || e.pointerType === 'touch') return;
            const item = e.target.closest('.ryvt-header__nav-item--has-flyout');
            if (item && !item.contains(e.relatedTarget)) {
              window.clearTimeout(this._flyoutCloseTimer);
              this._setFlyout(item, true);
            }
          };
          this._onPointerOut = (e) => {
            if (!this._desktopNav.matches || e.pointerType === 'touch') return;
            const item = e.target.closest('.ryvt-header__nav-item--has-flyout');
            if (item && !item.contains(e.relatedTarget)) {
              window.clearTimeout(this._flyoutCloseTimer);
              this._flyoutCloseTimer = window.setTimeout(() => this._setFlyout(item, false), 120);
            }
          };
          this._onFocusOut = (e) => {
            const item = e.target.closest('.ryvt-header__nav-item--has-flyout');
            if (item && !item.contains(e.relatedTarget)) this._setFlyout(item, false);
          };
          this._onFlyoutKeydown = (e) => {
            const trigger = e.target.closest('[data-ryvt-flyout-trigger]');
            if (trigger && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              const item = trigger.closest('.ryvt-header__nav-item--has-flyout');
              this._setFlyout(item, true);
              const links = Array.from(item.querySelectorAll('.ryvt-header__flyout a[href]'));
              const target = e.key === 'ArrowDown' ? links[0] : links[links.length - 1];
              if (target) target.focus();
              return;
            }
            if (e.key === 'Escape') {
              const openItem = this.querySelector('.ryvt-header__nav-item.is-flyout-open');
              if (!openItem) return;
              e.preventDefault();
              const openTrigger = openItem.querySelector('[data-ryvt-flyout-trigger]');
              this._setFlyout(openItem, false);
              if (openTrigger) openTrigger.focus();
            }
          };
          this._onDocumentClick = (e) => {
            if (!this.contains(e.target)) this._closeFlyouts();
          };
          this._onDesktopChange = () => this._closeFlyouts();
          this.addEventListener('pointerover', this._onPointerOver);
          this.addEventListener('pointerout', this._onPointerOut);
          this.addEventListener('focusout', this._onFocusOut);
          this.addEventListener('keydown', this._onFlyoutKeydown);
          document.addEventListener('click', this._onDocumentClick);
          this._desktopNav.addEventListener('change', this._onDesktopChange);

          this._onScroll = () => {
            this._syncPanelTop();
            if (this.hasAttribute('data-ryvt-transparent')) {
              this.classList.toggle('is-solid', window.scrollY > 64);
            }
          };
          window.addEventListener('scroll', this._onScroll, { passive: true });
          window.addEventListener('resize', this._syncPanelTop, { passive: true });
          this._onScroll();

          this._onWishlistChange = (detail) => {
            const count = detail && detail.handles ? detail.handles.length : 0;
            this.querySelectorAll('[data-ryvt-wishlist-count]').forEach((el) => {
              /* Always render the number. Blanking at zero left the nav drawer
                 showing a bare "Wishlist ( )" — the parentheses are markup, so
                 emptying only the span emptied the middle of the label. */
              crossfadeText(el, String(count));
            });
          };
          on('ryvt:wishlist:change', this._onWishlistChange);
          this._onWishlistChange({ handles: wishlist.list() });
        }

        disconnectedCallback() {
          this.removeEventListener('click', this._onClick);
          this.removeEventListener('pointerover', this._onPointerOver);
          this.removeEventListener('pointerout', this._onPointerOut);
          this.removeEventListener('focusout', this._onFocusOut);
          this.removeEventListener('keydown', this._onFlyoutKeydown);
          document.removeEventListener('click', this._onDocumentClick);
          if (this._desktopNav) this._desktopNav.removeEventListener('change', this._onDesktopChange);
          window.clearTimeout(this._flyoutCloseTimer);
          if (this._closeFlyouts) this._closeFlyouts();
          if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
          if (this._syncPanelTop) window.removeEventListener('resize', this._syncPanelTop);
          off('ryvt:wishlist:change', this._onWishlistChange);
        }
      }
    );
  }

  /* -- global delegation: wishlist toggles, quick-add, cart trigger, swatches - */
  document.addEventListener('click', (e) => {
    const wishlistToggle = e.target.closest('[data-ryvt-wishlist-toggle]');
    if (wishlistToggle) {
      e.preventDefault();
      const handle = wishlistToggle.getAttribute('data-ryvt-wishlist-toggle');
      const list = wishlist.toggle(handle);
      track('wishlist_toggle', {
        product_handle: handle,
        saved: list.indexOf(handle) !== -1,
        item_count: list.length,
        source: sectionSource(wishlistToggle),
      });
      return;
    }

    const quickAdd = e.target.closest('[data-ryvt-quick-add]');
    if (quickAdd && !quickAdd.disabled) {
      e.preventDefault();
      const card = quickAdd.closest('[data-ryvt-card]');
      const row = card && card.querySelector('[data-ryvt-size-row]');
      /* A sized product carries no variant id on the trigger — the ids live on
         the chips — so open the row instead of adding. Single-variant products
         keep the id on the trigger and add in one tap, exactly as before. */
      if (row) {
        renderCardSizeRow(card);
        quickAdd.hidden = true;
        row.hidden = false;
        const firstChip = row.querySelector('[data-ryvt-size-chip]:not([disabled])');
        if (firstChip) firstChip.focus();
        return;
      }
      const variantId = Number(quickAdd.getAttribute('data-variant-id'));
      if (!variantId) return;
      cardAdd(quickAdd, card, variantId);
      return;
    }

    const sizeChip = e.target.closest('[data-ryvt-size-chip]');
    if (sizeChip && !sizeChip.disabled) {
      e.preventDefault();
      const variantId = Number(sizeChip.getAttribute('data-variant-id'));
      if (!variantId) return;
      cardAdd(sizeChip, sizeChip.closest('[data-ryvt-card]'), variantId);
      return;
    }

    const sizeClose = e.target.closest('[data-ryvt-size-close]');
    if (sizeClose) {
      e.preventDefault();
      closeCardSizeRow(sizeClose.closest('[data-ryvt-card]'), true);
      return;
    }

    const cartTrigger = e.target.closest('[data-ryvt-cart-open]');
    if (cartTrigger) {
      const drawer = document.querySelector('[data-ryvt-section="ryvt-cart-drawer"]');
      if (drawer && typeof drawer.open === 'function') {
        e.preventDefault();
        drawer.open(cartTrigger);
      }
      return;
    }

    const swatch = e.target.closest('[data-ryvt-swatch]');
    if (swatch) {
      const card = swatch.closest('[data-ryvt-card]');
      if (!card) return;
      e.preventDefault();
      applyCardSwatch(card, swatch);
      /* Remember it, so browser-back onto the grid does not throw the choice
         away. Session-scoped on purpose: this is a within-visit browsing state,
         not a preference worth surviving a closed tab. */
      storeCardColor(card.dataset.handle, swatch.getAttribute('data-ryvt-swatch'));
    }
  });

  /* One body, three callers: the click above, the restore on load, and the
     restore on bfcache resume. Keeping it single means the restored state
     cannot drift from the clicked state. */
  /* One add path for both the single-variant trigger and a size chip, so the
     disable/re-enable and the error surface cannot drift between them. The cart
     drawer opens itself on action === 'add' (ryvt-cart.js), so nothing here
     opens it. */
  function cardAdd(button, card, variantId) {
    button.disabled = true;
    cart('add', { items: [{ id: variantId, quantity: 1 }] }, 'product_card')
      .then(() => {
        button.disabled = false;
        closeCardSizeRow(card, false);
      })
      .catch((error) => {
        button.disabled = false;
        const host = card || button.parentElement;
        const alert = host && host.querySelector('[data-ryvt-alert]');
        if (alert) {
          alert.textContent = (error && error.message) || 'Something went wrong.';
          alert.hidden = false;
        }
      });
  }

  function closeCardSizeRow(card, focusTrigger) {
    if (!card) return;
    const row = card.querySelector('[data-ryvt-size-row]');
    const trigger = card.querySelector('[data-ryvt-quick-add]');
    if (row) row.hidden = true;
    if (trigger) {
      trigger.hidden = false;
      /* Only on an explicit close. After a successful add the cart drawer takes
         focus, and pulling it back to the card would fight the drawer. */
      if (focusTrigger) trigger.focus();
    }
  }

  /* Rebuilds the chip row for the card's ACTIVE colour. This is what stops the
     row ever offering a size in a colour the shopper is not looking at — the
     failure R1 named when it cut card quick-add. */
  function renderCardSizeRow(card) {
    const row = card && card.querySelector('[data-ryvt-size-row]');
    const raw = card && card.querySelector('[data-ryvt-card-variants]');
    if (!row || !raw) return;
    let data;
    try {
      data = JSON.parse(raw.textContent);
    } catch (e) {
      return; /* malformed payload: leave the server-rendered row as it stands */
    }
    const active = card.querySelector('[data-ryvt-swatch].is-active');
    /* Normalised to null, never ''. A product with no Color option stamps
       sizeLabel: null, but an empty-string colour would be falsy AND fail the
       `=== null` guard below, disabling every chip on the card. */
    const color = (active ? active.getAttribute('data-ryvt-swatch') : data.sizeLabel) || null;

    const label = row.querySelector('[data-ryvt-size-label]');
    if (label) {
      const tpl = row.getAttribute('data-str-size-prompt') || '';
      label.textContent = color
        ? tpl.replace('[color]', color)
        : row.getAttribute('data-str-size-prompt-nocolor') || '';
    }

    let purchasable = 0;
    row.querySelectorAll('[data-ryvt-size-chip]').forEach((chip) => {
      const size = chip.getAttribute('data-ryvt-size-chip');
      const match = (data.variants || []).find(
        (v) =>
          v.size === size &&
          (color === null || v.color === null || v.color === color) &&
          v.available
      );
      if (match) {
        purchasable += 1;
        chip.setAttribute('data-variant-id', String(match.id));
        chip.disabled = false;
        chip.classList.remove('is-unavailable');
      } else {
        chip.removeAttribute('data-variant-id');
        chip.disabled = true;
        chip.classList.add('is-unavailable');
      }
    });

    /* Every size gone in this colour: a row of struck-through chips reads as
       broken, so offer the PDP instead. The title link's href already carries
       ?color=, rewritten by applyCardSwatch, so this stays in step with it. */
    const chips = row.querySelector('[data-ryvt-size-chips]');
    const options = row.querySelector('[data-ryvt-size-options]');
    if (chips) chips.hidden = purchasable === 0;
    if (options) {
      options.hidden = purchasable !== 0;
      const titleLink = card.querySelector('.ryvt-card__link');
      if (titleLink) options.setAttribute('href', titleLink.getAttribute('href'));
    }
  }

  function applyCardSwatch(card, swatch) {
    /* BOTH images, not just the primary. This swapped only .ryvt-media__img,
       which is the one image guaranteed NOT to be on screen at the moment a
       swatch is clicked: reaching the swatch with a mouse means the card is
       :hover, and :hover puts .ryvt-card__ghost at opacity 1 covering the
       primary completely. So the colour appeared not to change at all — the
       swap was landing underneath the ghost every time. */
    swapCardImage(card.querySelector('.ryvt-media__img'), swatch.getAttribute('data-primary-url'));
    swapCardImage(card.querySelector('.ryvt-card__ghost'), swatch.getAttribute('data-hover-url'));

    card.querySelectorAll('[data-ryvt-swatch]').forEach((dot) => {
      dot.classList.toggle('is-active', dot === swatch);
    });

    /* The whole point. The card used to swap its photograph and then hand the
       shopper a link to the default colourway, so the PDP opened on a garment
       they had just navigated away from. Both links carry it: the media link
       and the title link go to the same place and must agree.

       ?color= and NOT ?variant=: main-product.liquid derives size_chosen from
       product.selected_variant, so a variant id in the URL marks a size as
       chosen and arms Add to cart for a size the shopper never picked. Colour
       is the only thing a swatch actually said.

       Split on '?' rather than appending, so clicking a second swatch replaces
       the param instead of stacking a second one. */
    const color = swatch.getAttribute('data-ryvt-swatch');
    if (color) {
      card.querySelectorAll('.ryvt-card__media-link, .ryvt-card__link').forEach((a) => {
        const base = a.getAttribute('href').split('?')[0];
        a.setAttribute('href', base + '?color=' + encodeURIComponent(color));
      });
    }

    /* Last, so the href rewrite above has already run — renderCardSizeRow copies
       the title link's href onto the See options fallback. Hooked here rather
       than in the click handler because this function is the single funnel for
       all three colour paths (click, restore on load, bfcache resume); anywhere
       else and the row could offer sizes for a colour the card is not showing. */
    renderCardSizeRow(card);
  }

  const CARD_COLOR_KEY = 'ryvt:card-color';

  /* Storage throws in private modes and when a shopper has blocked it. A lost
     swatch memory is not worth a dead click handler, so every access is
     guarded and every failure is silent. */
  function readCardColors() {
    try {
      return JSON.parse(sessionStorage.getItem(CARD_COLOR_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function storeCardColor(handle, color) {
    if (!handle || !color) return;
    try {
      const all = readCardColors();
      all[handle] = color;
      sessionStorage.setItem(CARD_COLOR_KEY, JSON.stringify(all));
    } catch (e) {
      /* storage unavailable — the swatch still works, it just will not persist */
    }
  }

  /* Only queries cards present in the DOM at call time. Cards injected later —
     the wishlist grid hydrating through the Section Rendering API, or a
     predictive search result — never get a pass, so a remembered colour
     will not apply to them (the swatch click itself still works everywhere,
     since that handler is delegated). Accepted scope; see review notes. */
  function restoreCardColors() {
    const all = readCardColors();
    document.querySelectorAll('[data-ryvt-card]').forEach((card) => {
      const want = all[card.dataset.handle];
      if (!want) return;
      const swatch = Array.prototype.find.call(
        card.querySelectorAll('[data-ryvt-swatch]'),
        (s) => s.getAttribute('data-ryvt-swatch') === want
      );
      /* A colourway can be discontinued between one page view and the next.
         No matching swatch means the memory is stale — leave the card at its
         default rather than half-applying. */
      if (swatch) applyCardSwatch(card, swatch);
    });
  }

  /* Both events, but not both unconditionally. DOMContentLoaded (or the
     immediate call, if it already fired) covers a normal load. pageshow
     covers the bfcache resume that browser-back actually uses, where
     DOMContentLoaded does not fire at all and the page is restored exactly
     as it was left — which, before this, meant restored with the default
     colour showing. pageshow ALSO fires on every normal load, right after
     the DOMContentLoaded path above already ran — gated on event.persisted
     (true only for a bfcache restore) so that case doesn't run twice. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreCardColors);
  } else {
    restoreCardColors();
  }
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) restoreCardColors();
  });

  function sectionSource(node) {
    const section = node && node.closest ? node.closest('[data-ryvt-section]') : null;
    return (section && section.getAttribute('data-ryvt-section')) || 'unknown';
  }

  function searchResultType(option) {
    const id = option && option.id ? option.id : '';
    if (id.indexOf('Option-p') !== -1) return 'product';
    if (id.indexOf('Option-c') !== -1) return 'collection';
    if (id.indexOf('Option-g') !== -1) return 'page';
    if (id.indexOf('Option-q') !== -1) return 'query';
    return 'all';
  }

  /* Navigation and native-form hooks are observational only: no default is
     prevented and no submission is delayed. This preserves the no-JS contract
     and avoids duplicating Shopify's own analytics transport. */
  document.addEventListener('click', (event) => {
    const target = event.target && event.target.closest ? event.target : null;
    if (!target) return;

    const searchOption = target.closest('[data-ryvt-search-option]');
    if (searchOption) {
      const host = searchOption.closest('ryvt-predictive-search');
      const input = host && host.querySelector('input[type="search"]');
      const options = host ? Array.from(host.querySelectorAll('[data-ryvt-search-option]')) : [];
      track('search_select', {
        source: 'predictive',
        result_type: searchResultType(searchOption),
        position: options.indexOf(searchOption) + 1,
        query_length: input ? input.value.trim().length : 0,
      });
      return;
    }

    const productLink = target.closest('[data-ryvt-card] a[href]');
    if (productLink) {
      const card = productLink.closest('[data-ryvt-card]');
      const cards = Array.from(
        (card.closest('[role="list"]') || document).querySelectorAll('[data-ryvt-card]')
      );
      const source = sectionSource(card);
      track('discovery_select', {
        source,
        destination: 'product',
        product_handle: card.getAttribute('data-handle'),
        position: cards.indexOf(card) + 1,
      });
      if (source === 'main-search') {
        const input = document.querySelector('[data-ryvt-section="main-search"] input[name="q"]');
        track('search_select', {
          source: 'results_page',
          result_type: 'product',
          position: cards.indexOf(card) + 1,
          query_length: input ? input.value.trim().length : 0,
        });
      }
      return;
    }

    const otherSearchResult = target.closest(
      '[data-ryvt-section="main-search"] .ryvt-search__row-link'
    );
    if (otherSearchResult) {
      const group = otherSearchResult.closest('.ryvt-search__group');
      const links = group ? Array.from(group.querySelectorAll('.ryvt-search__row-link')) : [];
      const type = otherSearchResult.querySelector('.ryvt-search__row-type');
      const input = document.querySelector('[data-ryvt-section="main-search"] input[name="q"]');
      track('search_select', {
        source: 'results_page',
        result_type: type ? type.textContent.trim().toLowerCase() : 'other',
        position: links.indexOf(otherSearchResult) + 1,
        query_length: input ? input.value.trim().length : 0,
      });
      return;
    }

    const categoryLink = target.closest(
      '[data-ryvt-section="ryvt-drop-grid"] .ryvt-drop-grid__category-link, [data-ryvt-section="main-collection"] .ryvt-plp-chips__chip'
    );
    if (categoryLink) {
      track('discovery_select', {
        source: sectionSource(categoryLink),
        destination: 'collection',
      });
      return;
    }

  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const searchInput = form.querySelector('input[type="search"][name="q"]');
    if (searchInput) {
      track('search_submit', {
        source: sectionSource(form),
        query_length: searchInput.value.trim().length,
      });
    }
    const tags = form.querySelector('input[name="contact[tags]"]');
    if (tags && tags.value.split(',').map((tag) => tag.trim()).indexOf('newsletter') !== -1) {
      track('newsletter_submit', { source: sectionSource(form) });
    }
    const submitter = event.submitter;
    if (submitter && submitter.name === 'checkout') {
      track('checkout_start', { source: sectionSource(form) });
    }
  });

  function trackInitialSearchResults() {
    const section = document.querySelector('[data-ryvt-section="main-search"]');
    if (!section) return;
    const input = section.querySelector('input[type="search"][name="q"]');
    const queryLength = input ? input.value.trim().length : 0;
    if (!queryLength) return;
    const count = Array.from(section.querySelectorAll('[data-result-count]')).reduce(
      (sum, group) => sum + (Number(group.getAttribute('data-result-count')) || 0),
      0
    );
    track('search_results', {
      source: 'results_page',
      query_length: queryLength,
      result_count: count,
    });
  }

  /* Decode before swapping. The ghost is already at full opacity while the
     cursor is on the swatch, so assigning src directly paints a blank frame
     until the new file arrives — the same flash the C10 decode gate below
     exists to prevent on first hover. Preloading a detached Image and only
     then assigning keeps the old frame up until the new one can paint.
     srcset has to go: it was authored for the previous colour and would win
     over the src we just set. */
  function swapCardImage(img, url) {
    if (!img || !url || img.getAttribute('src') === url) return;
    const apply = () => {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
    };
    const pre = new Image();
    pre.src = url;
    if (pre.decode) {
      pre.decode().then(apply).catch(apply);
    } else {
      apply();
    }
  }

  /* -- card ghost decode gate (C10) ------------------------------------------ */
  document.addEventListener(
    'pointerenter',
    (e) => {
      if (!e.target || !e.target.closest) return;
      const card = e.target.closest('[data-ryvt-card]');
      if (!card || card.hasAttribute('data-ghost-ready')) return;
      const ghost = card.querySelector('.ryvt-card__ghost');
      if (!ghost) return;
      const ready = () => card.setAttribute('data-ghost-ready', '');
      if (ghost.decode) {
        ghost.decode().then(ready).catch(() => {});
      } else {
        ready();
      }
    },
    true
  );

  /* -- reveal IO (C9) --------------------------------------------------------
     Two gates, not one. The observer only says the node has arrived on screen;
     the <img> inside it is loading="lazy" and may still be in flight. Revealing
     on intersection alone meant the curtain wiped away over 560ms to expose an
     empty box, and the photograph popped in afterwards -- the single loudest
     reason the page felt raw on load.

     A figure whose image ERRORS still reveals. Never trap content behind a
     failed asset: an alt-text box in the layout beats a permanent blank. */
  /* Nodes this runtime has already taken responsibility for. initReveals may be
     entered more than once (see the shopify:section:load handler below), and a
     node must not be re-observed or re-staggered on a second pass. */
  const revealSeen = new WeakSet();

  function initReveals() {
    /* Claim motion FIRST, before any early return. layout/theme.liquid watches
       for this class on `load` and reveals everything if it never appears --
       `html.js` only proves scripting is on, not that this file ran. Claiming it
       after an early return would trip the failsafe on any page with no reveal
       nodes and leave the class permanently unset. */
    document.documentElement.classList.add('ryvt-motion');

    const nodes = document.querySelectorAll('[data-ryvt-reveal], [data-ryvt-rise]');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-revealed'));
      return;
    }

    /* Stagger is per-parent, not per-document: siblings in one grid step
       together, and a second grid further down starts again at 0 rather than
       inheriting a delay from everything above it. Capped at 6 so a 20-card
       grid finishes in 360ms.

       A card's real stagger group is not always its immediate parentElement:
       every grid (drop grid, PLP, search, complete-the-look, wishlist) wraps
       each `.ryvt-card` in a single-item `<li>`/`<div>` cell, so the direct
       parent never has more than one reveal/rise CHILD even though the grid
       plainly has many. Climb exactly one level past a single-child wrapper
       so the cell, not the card, is what gets compared against its siblings
       -- matched by containment, since the attribute lives on the card inside
       it, not the cell itself. */
    function assignStagger(node) {
      let unit = node;
      let scope = unit.parentElement;
      if (scope && scope.children.length === 1 && scope.parentElement) {
        unit = scope;
        scope = scope.parentElement;
      }
      if (!scope) return;
      const siblings = [...scope.children].filter(
        (el) =>
          el === unit ||
          el.hasAttribute('data-ryvt-reveal') ||
          el.hasAttribute('data-ryvt-rise') ||
          el.querySelector('[data-ryvt-reveal],[data-ryvt-rise]')
      );
      if (siblings.length < 2) return;
      const i = siblings.indexOf(unit);
      node.style.setProperty('--ryvt-reveal-i', String(Math.min(i, 6)));
    }

    function imagesReady(node) {
      const imgs = [...node.querySelectorAll('img')];
      if (!imgs.length) return Promise.resolve();
      return Promise.all(
        imgs.map((img) => {
          /* `complete` alone, NOT `complete && naturalWidth > 0`. An image that
             already ERRORED is also complete, with naturalWidth 0 -- and in a
             browser without decode() that fell through to the listener branch
             below, where load and error had both already fired, so the promise
             never settled and the node stayed curtained for good. Nothing more
             is going to happen to a complete image either way, so resolve. */
          if (img.complete) return Promise.resolve();
          /* decode() rejects on a broken image and on some Safari versions for
             images that load fine, so every path falls through to resolve. */
          if (typeof img.decode === 'function') {
            return img.decode().catch(() => undefined);
          }
          return new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          });
        })
      );
    }

    function reveal(node) {
      /* The image-decode gate exists for the curtain: that wipe uncovers a
         photograph, and revealing it before the photograph is there shows an
         empty box. Content rise has no such photograph to wait on -- and a
          so gating rises on imagesReady() would stall text behind unrelated
          photography. Only nodes actually carrying the curtain wait. */
      if (node.hasAttribute('data-ryvt-reveal')) {
        return imagesReady(node).then(() => node.classList.add('is-revealed'));
      }
      node.classList.add('is-revealed');
      return Promise.resolve();
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    nodes.forEach((node) => {
      if (revealSeen.has(node)) return;
      revealSeen.add(node);
      assignStagger(node);
      /* Already on screen at init: reveal with transitions suppressed for one
         frame, then hand the node back so later state changes still animate. */
      const box = node.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        node.classList.add('is-instant');
        reveal(node).then(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => node.classList.remove('is-instant'));
          });
        });
        return;
      }
      io.observe(node);
    });
  }

  /* -- announcement rotation (200ms crossfade every 6s; static reduced) -------
     Pauses under the pointer or keyboard focus (redesign, closing the Aug 19
     "unpausable rotation" finding): a line someone is reading never rotates
     out from under them. Resumes when attention leaves. */
  function initAnnouncement() {
    const items = document.querySelectorAll('[data-ryvt-announcement-item]');
    if (items.length < 2 || reducedMotionQuery.matches) return;
    const bar = items[0].closest('[data-ryvt-section="ryvt-announcement"]');
    let index = 0;
    let held = false;
    if (bar) {
      const hold = () => { held = true; };
      const release = () => { held = false; };
      bar.addEventListener('mouseenter', hold);
      bar.addEventListener('mouseleave', release);
      bar.addEventListener('focusin', hold);
      bar.addEventListener('focusout', release);
    }
    window.setInterval(() => {
      if (held) return;
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, 6000);
  }

  initReveals();

  /* The theme editor re-renders a whole section on every setting change, and the
     replacement nodes arrive without `is-revealed` and unobserved by the
     IntersectionObserver built above -- so the buy column, the footer or a card
     grid simply goes blank until a hard reload. That is the surface the owner
     reviews this theme on.

     Revealed INSTANTLY rather than re-observed: the editor is not a first-visit
     context, and replaying an entrance animation on every slider drag is noise.
     `is-instant` is dropped two frames later so later state changes still
     animate normally.

     This deliberately does NOT re-enter initReveals(): that would build a second
     IntersectionObserver on every edit and leak one per change. Nothing else is
     needed -- these nodes are already revealed. */
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (!root || typeof root.querySelectorAll !== 'function') return;
    const selector = '[data-ryvt-reveal], [data-ryvt-rise]';
    const fresh = [...root.querySelectorAll(selector)];
    if (root.matches && root.matches(selector)) fresh.push(root);
    fresh.forEach((node) => {
      revealSeen.add(node);
      node.classList.add('is-instant', 'is-revealed');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => node.classList.remove('is-instant'));
      });
    });
  });
  initAnnouncement();
  trackInitialSearchResults();
  syncWishlistToggles(wishlistRead());
  wishlistPullAndReconcile();

  /* -- public API ------------------------------------------------------------- */
  window.RYVT = {
    on,
    off,
    emit,
    track,
    trackCartResult,
    cart,
    renderSections,
    trapFocus,
    crossfadeText,
    wishlist,
    RyvtDrawer,
    reducedMotion: () => reducedMotionQuery.matches,
  };
  window.RyvtDrawer = RyvtDrawer;
})();
